import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  CheckCircle2,
  Loader2,
  Save,
  Sparkles,
  Trophy,
} from 'lucide-react';
import { ModuleHero } from '../components/ModuleHero';
import { Switch } from '../components/Switch';
import {
  clearDraft,
  emptyDraft,
  loadDraft,
  saveDraft,
  type TournamentDraft,
} from '../services/tournamentDraft';
import {
  createTournament,
  createVenue,
  defaultPlayoffSettings,
  defaultRollerSeasonSettings,
} from '../services/tournamentService';
import { ensureRinksForTournament } from '../services/schedulerService';
import { useStore } from '../hooks/useStore';
import { US_STATES } from '../lib/usStates';
import { paths, tournamentSlug } from '../lib/paths';
import type { CompetitionFormat, ScoringSettings, TournamentSettings, Weekday } from '../models';

const TOURNAMENT_STEPS = ['Type', 'Details', 'Venue', 'Review'] as const;
const SEASON_STEPS = ['Details', 'Rink', 'Cadence', 'Rules', 'Playoffs', 'Review'] as const;

const WEEKDAYS: Array<{ value: Weekday; label: string }> = [
  { value: 'mon', label: 'Mon' },
  { value: 'tue', label: 'Tue' },
  { value: 'wed', label: 'Wed' },
  { value: 'thu', label: 'Thu' },
  { value: 'fri', label: 'Fri' },
  { value: 'sat', label: 'Sat' },
  { value: 'sun', label: 'Sun' },
];

const TIEBREAKER_LABEL: Record<ScoringSettings['tiebreakers'][number], string> = {
  wins: 'Wins',
  goal_diff: 'Goal differential',
  goals_for: 'Goals for',
  head_to_head: 'Head-to-head',
  shootout_wins: 'Shootout wins',
  fewest_pims: 'Fewest penalty minutes',
};

const PLAYOFF_OPTIONS: Array<{
  value: TournamentDraft['playoffFormat'];
  title: string;
  description: string;
}> = [
  {
    value: 'single_elim',
    title: 'Single elimination',
    description: 'One loss ends a team\'s playoff run. Simple, fast, and familiar.',
  },
  {
    value: 'double_elim',
    title: 'Double elimination',
    description: 'Teams stay alive until their second playoff loss. Better for bigger brackets.',
  },
  {
    value: 'top_n_round_robin',
    title: 'Top teams round robin',
    description: 'Qualified teams play a small playoff pool before the final placement.',
  },
];

interface CreatePageProps {
  presetFormat?: CompetitionFormat;
}

type StepValidationCode =
  | 'type'
  | 'name'
  | 'dates'
  | 'venueName'
  | 'venueAddress'
  | 'rinkCount'
  | 'weekdays'
  | 'firstPuck'
  | 'gamesPerNight'
  | 'rules'
  | 'playoffTeams';

interface StepValidation {
  code: StepValidationCode;
  message: string;
}

export function TournamentCreatePage({ presetFormat }: CreatePageProps = {}) {
  const navigate = useNavigate();
  const db = useStore((d) => d);
  const types = useMemo(() => [...db.tournamentTypes], [db.tournamentTypes]);
  const [draft, setDraft] = useState<TournamentDraft>(() => {
    const existing = loadDraft();
    const initial =
      existing && (!presetFormat || existing.format === presetFormat)
        ? existing
        : emptyDraft(presetFormat ?? 'tournament', db.league);
    const initialSteps = initial.format === 'season' ? SEASON_STEPS : TOURNAMENT_STEPS;
    return normalizeStep(initial, initialSteps.length);
  });
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [stepErrors, setStepErrors] = useState<StepValidation[]>([]);

  const isSeason = draft.format === 'season';
  const steps = isSeason ? SEASON_STEPS : TOURNAMENT_STEPS;
  const stepName = steps[Math.min(draft.step, steps.length - 1)];
  const seasonTypes = types.filter((t) => t.typeId.includes('season') && t.typeId !== 'ironman-season');
  const tournamentTypes = types.filter((t) => !t.typeId.includes('season'));
  const visibleTypes = isSeason ? seasonTypes : tournamentTypes;

  const update = (patch: Partial<TournamentDraft>) => {
    setDraft((prev) => {
      const next = normalizeStep({ ...prev, ...patch }, steps.length);
      saveDraft(next);
      return next;
    });
  };

  const setFormat = (format: CompetitionFormat) => {
    const next = emptyDraft(format, db.league);
    update({ ...next, step: 0 });
  };

  const setWeekday = (weekday: Weekday, checked: boolean) => {
    const next = checked
      ? Array.from(new Set([...draft.seasonWeekdays, weekday]))
      : draft.seasonWeekdays.filter((day) => day !== weekday);
    update({ seasonWeekdays: next });
  };

  const goNext = () => update({ step: Math.min(draft.step + 1, steps.length - 1) });
  const goPrev = () => update({ step: Math.max(draft.step - 1, 0) });

  useEffect(() => {
    setStepErrors([]);
  }, [draft.step]);

  const continueOrShowErrors = () => {
    const errors = validateStep(draft, stepName);
    setStepErrors(errors);
    if (errors.length > 0) return;
    goNext();
  };

  const finalize = () => {
    if (isFinalizing) return;
    const errors = validateDraft(draft, steps);
    if (errors.length > 0) {
      setStepErrors(errors);
      const firstInvalidStep = steps.findIndex((step) => validateStep(draft, step).length > 0);
      if (firstInvalidStep >= 0) update({ step: firstInvalidStep });
      return;
    }
    setIsFinalizing(true);

    window.setTimeout(() => {
      const selectedType = types.find((type) => type.typeId === draft.typeId);
      const baseSettings =
        selectedType?.defaults ??
        (draft.format === 'season' ? defaultRollerSeasonSettings() : undefined);
      const settings = buildSettings(draft, baseSettings);

      const venue = createVenue({
        name: draft.venueName,
        address: draft.venueAddress,
        postalCode: draft.venuePostalCode,
        city: draft.venueCity,
        region: draft.venueRegion,
      });
      const competition = createTournament({
        name: draft.name,
        typeId: draft.typeId,
        startsAt: draft.startsAt,
        endsAt: draft.endsAt,
        tagline: draft.tagline,
        primaryVenueId: venue.id,
        format: draft.format,
        settings,
      });
      ensureRinksForTournament(competition.id, draft.rinkCount, db.league.eventDefaults.rinkNamePattern);
      clearDraft();

      const destination = tournamentSlug(competition);
      navigate(paths.adminCompetition(competition.format, destination));
    }, 80);
  };

  return (
    <>
      <ModuleHero
        eyebrow={isSeason ? 'New season' : 'New tournament'}
        title={isSeason ? 'Set up a roller hockey season.' : "Let's set up your tournament."}
        description={
          isSeason
            ? 'A guided setup for the basics: dates, rink, cadence, roster intake, game structure, and playoffs. You can keep editing before publish.'
            : 'A short, friendly walkthrough. Save and come back anytime; your draft is kept locally until you create it.'
        }
        icon={isSeason ? CalendarDays : Trophy}
        accent="arc"
        status={{ label: 'Draft auto-saved', tone: 'ok' }}
      >
        {!isSeason && (
          <div className="row">
            <Link to={paths.adminTournaments} className="btn btn--ghost btn--sm">
              <ArrowLeft size={14} /> Back to tournaments
            </Link>
            <button
              className="btn btn--ghost btn--sm"
              onClick={() => {
                clearDraft();
                setDraft(emptyDraft(draft.format, db.league));
              }}
            >
              Start over
            </button>
          </div>
        )}
      </ModuleHero>

      <div className="card">
        <div className="steps-compact" aria-label={`Step ${draft.step + 1} of ${steps.length}`}>
          <div className="steps-compact__meta">
            <span>Step {draft.step + 1} of {steps.length}</span>
            <strong>{stepName}</strong>
          </div>
          <div
            className="steps-compact__bar"
            role="progressbar"
            aria-valuemin={1}
            aria-valuemax={steps.length}
            aria-valuenow={draft.step + 1}
            aria-label={`${stepName} setup progress`}
          >
            <span style={{ width: `${((draft.step + 1) / steps.length) * 100}%` }} />
          </div>
        </div>

        <div className="steps" role="list">
          {steps.map((label, i) => (
            <div
              key={label}
              role="listitem"
              className={`steps__item ${i === draft.step ? 'is-active' : i < draft.step ? 'is-done' : ''}`}
            >
              <span className="steps__num">
                {i < draft.step ? <CheckCircle2 size={12} /> : i + 1}
              </span>
              <span>{label}</span>
              {i < steps.length - 1 && <span className="steps__sep" aria-hidden />}
            </div>
          ))}
        </div>

        {stepErrors.length > 0 && (
          <div className="form-warning" role="alert">
            <strong>Finish this step before continuing.</strong>
            <ul>
              {stepErrors.map((error) => <li key={`${error.code}-${error.message}`}>{error.message}</li>)}
            </ul>
          </div>
        )}

        {stepName === 'Type' && (
          <TypeStep
            draft={draft}
            presetFormat={presetFormat}
            visibleTypes={visibleTypes}
            onFormat={setFormat}
            onType={(typeId) => {
              const type = types.find((candidate) => candidate.typeId === typeId);
              update({
                typeId,
                ...(type?.defaults ? draftFromSettings(draft, type.defaults) : {}),
              });
            }}
          />
        )}

        {stepName === 'Details' && <DetailsStep draft={draft} update={update} errors={stepErrors} />}
        {(stepName === 'Venue' || stepName === 'Rink') && <VenueStep draft={draft} update={update} errors={stepErrors} />}
        {stepName === 'Cadence' && <CadenceStep draft={draft} update={update} errors={stepErrors} setWeekday={setWeekday} />}
        {stepName === 'Rules' && <RulesStep draft={draft} update={update} errors={stepErrors} />}
        {stepName === 'Playoffs' && <PlayoffsStep draft={draft} update={update} errors={stepErrors} />}
        {stepName === 'Review' && (
          <ReviewStep
            draft={draft}
            typeLabel={types.find((x) => x.typeId === draft.typeId)?.label ?? draft.typeId}
          />
        )}

        <div className="row" style={{ marginTop: 22, justifyContent: 'space-between' }}>
          <button className="btn btn--ghost" onClick={goPrev} disabled={draft.step === 0}>
            <ArrowLeft size={14} /> Back
          </button>
          <div className="row" style={{ gap: 8 }}>
            <button className="btn btn--ghost btn--sm" onClick={() => saveDraft(draft)}>
              <Save size={14} /> Save draft
            </button>
            {draft.step < steps.length - 1 ? (
              <button className="btn btn--primary" onClick={continueOrShowErrors}>
                Continue <ArrowRight size={14} />
              </button>
            ) : (
              <button className={`btn btn--primary ${isFinalizing ? 'is-loading' : ''}`} onClick={finalize} disabled={isFinalizing}>
                {isFinalizing ? (
                  <>
                    <Loader2 size={14} className="spin" /> Creating draft {isSeason ? 'season' : 'tournament'}...
                  </>
                ) : (
                  <>
                    Create draft {isSeason ? 'season' : 'tournament'} <CheckCircle2 size={14} />
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function TypeStep({
  draft,
  presetFormat,
  visibleTypes,
  onFormat,
  onType,
}: {
  draft: TournamentDraft;
  presetFormat?: CompetitionFormat;
  visibleTypes: Array<{ typeId: string; label: string; description?: string }>;
  onFormat: (format: CompetitionFormat) => void;
  onType: (typeId: string) => void;
}) {
  const isSeason = draft.format === 'season';
  return (
    <div className="stack">
      {!presetFormat && (
        <div className="stack" style={{ marginBottom: 18 }}>
          <p className="muted" style={{ marginTop: 0 }}>Tournament block or recurring season?</p>
          <div className="segmented segmented--cols-2">
            <button type="button" className={`pick ${!isSeason ? 'is-active' : ''}`} onClick={() => onFormat('tournament')}>
              <div className="pick__title">Tournament</div>
              <div className="pick__sub">A compressed event: weekend, week, or single block of play.</div>
            </button>
            <button type="button" className={`pick ${isSeason ? 'is-active' : ''}`} onClick={() => onFormat('season')}>
              <div className="pick__title">Season</div>
              <div className="pick__sub">Recurring weekly play with standings, roster intake, and optional playoffs.</div>
            </button>
          </div>
        </div>
      )}
      <p className="muted">
        {isSeason
          ? 'Choose the season preset that best matches how your league plays. Every rule can be adjusted later.'
          : 'Choose the tournament rule preset. You can adjust scoring and scheduling after the draft is created.'}
      </p>
      <div className="segmented segmented--cols-2">
        {visibleTypes.map((t) => (
          <button
            key={t.typeId}
            type="button"
            className={`pick ${draft.typeId === t.typeId ? 'is-active' : ''}`}
            onClick={() => onType(t.typeId)}
          >
            <div className="pick__title">{t.label}</div>
            <div className="pick__sub">{t.description}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function DetailsStep({ draft, errors, update }: StepProps) {
  const isSeason = draft.format === 'season';
  return (
    <div className="stack">
      <div className="field">
        <FieldLabel required help={isSeason ? 'Use the plain name people will recognize in schedules, standings, registration, and public pages.' : undefined}>
          {isSeason ? 'Season name' : 'Tournament name'}
        </FieldLabel>
        <input
          className="input"
          value={draft.name}
          onChange={(e) => update({ name: e.target.value })}
          placeholder={isSeason ? 'Spring 2026 Roller Hockey League' : 'Spring Ironman Jam'}
          aria-invalid={hasError(errors, 'name')}
        />
        {hasError(errors, 'name') && <span className="field__error">Name is required.</span>}
      </div>
      <div className="field">
        <FieldLabel optional help="A short subtitle shown under the event name on public pages. Leave it blank if the name is enough.">
          Public subtitle
        </FieldLabel>
        <input
          className="input"
          value={draft.tagline ?? ''}
          onChange={(e) => update({ tagline: e.target.value })}
          placeholder={isSeason ? 'Weekly roller hockey from puck drop to playoffs.' : 'Three days. Twelve-minute battles.'}
        />
      </div>
      <div className="grid grid--2">
        <div className="field">
          <FieldLabel required help={isSeason ? 'The first calendar day this season should be considered active. Game times are handled later in schedule setup.' : undefined}>
            {isSeason ? 'Season starts' : 'Start'}
          </FieldLabel>
          <input
            type={isSeason ? 'date' : 'datetime-local'}
            className="input"
            value={isSeason ? dateOnly(draft.startsAt) : toLocal(draft.startsAt)}
            onChange={(e) => update({ startsAt: isSeason ? fromDate(e.target.value) : fromLocal(e.target.value) })}
            aria-invalid={hasError(errors, 'dates')}
          />
        </div>
        <div className="field">
          <FieldLabel required help={isSeason ? 'The final calendar day for the season window. Playoffs can still be configured in the playoff step.' : undefined}>
            {isSeason ? 'Season ends' : 'End'}
          </FieldLabel>
          <input
            type={isSeason ? 'date' : 'datetime-local'}
            className="input"
            value={isSeason ? dateOnly(draft.endsAt) : toLocal(draft.endsAt)}
            onChange={(e) => update({ endsAt: isSeason ? fromDate(e.target.value) : fromLocal(e.target.value) })}
            aria-invalid={hasError(errors, 'dates')}
          />
        </div>
      </div>
      {hasError(errors, 'dates') && <span className="field__error">Start and end dates are required.</span>}
    </div>
  );
}

function VenueStep({ draft, errors, update }: StepProps) {
  const isSeason = draft.format === 'season';
  const onZip = async (value: string) => {
    const zip = value.replace(/\D+/g, '').slice(0, 5);
    update({ venuePostalCode: zip });
    if (zip.length !== 5) return;
    const match = await lookupZip(zip);
    if (match) update({ venuePostalCode: zip, venueCity: match.city, venueRegion: match.stateCode });
  };
  return (
    <div className="stack">
      {isSeason && (
        <p className="muted">
          This is prefilled from Settings when a home rink is configured. Adjust it here only if this season is using a different rink.
        </p>
      )}
      <div className="field">
        <FieldLabel required help="The rink or facility name that appears on schedules, public pages, and registration pages.">
          Rink name
        </FieldLabel>
        <input
          className="input"
          value={draft.venueName}
          onChange={(e) => update({ venueName: e.target.value })}
          placeholder="Rinkwerks North"
          aria-invalid={hasError(errors, 'venueName')}
        />
        {hasError(errors, 'venueName') && <span className="field__error">Rink name is required.</span>}
      </div>
      <div className="field">
        <FieldLabel required help="Used for public pages, registration context, and future calendar/location links.">
          Rink address
        </FieldLabel>
        <input
          className="input"
          value={draft.venueAddress}
          onChange={(e) => update({ venueAddress: e.target.value })}
          placeholder="1101 Rink Way"
          aria-invalid={hasError(errors, 'venueAddress')}
        />
        {hasError(errors, 'venueAddress') && <span className="field__error">Rink address is required.</span>}
      </div>
      <div className="grid grid--3">
        <div className="field">
          <FieldLabel optional>ZIP</FieldLabel>
          <input
            className="input"
            inputMode="numeric"
            value={draft.venuePostalCode ?? ''}
            onChange={(e) => void onZip(e.target.value)}
            placeholder="12345"
          />
        </div>
        <div className="field">
          <FieldLabel optional>City</FieldLabel>
          <input className="input" value={draft.venueCity ?? ''} onChange={(e) => update({ venueCity: e.target.value })} />
        </div>
        <div className="field">
          <FieldLabel optional>State</FieldLabel>
          <select className="select" value={draft.venueRegion ?? ''} onChange={(e) => update({ venueRegion: e.target.value })}>
            <option value="">Select a state</option>
            {US_STATES.map((state) => <option key={state.code} value={state.code}>{state.name}</option>)}
          </select>
        </div>
      </div>
      {!isSeason && (
        <div className="grid grid--3">
          <div className="field">
            <FieldLabel required>Rinks</FieldLabel>
            <input
              type="number"
              min={1}
              max={12}
              className="input"
              value={draft.rinkCount}
              onChange={(e) => update({ rinkCount: clampInt(e.target.value, 1, 12) })}
              aria-invalid={hasError(errors, 'rinkCount')}
            />
            {hasError(errors, 'rinkCount') && <span className="field__error">At least one rink is required.</span>}
          </div>
        </div>
      )}
    </div>
  );
}

function CadenceStep({ draft, errors, update, setWeekday }: StepProps & { setWeekday: (weekday: Weekday, checked: boolean) => void }) {
  return (
    <div className="stack">
      <p className="muted">Set the default rhythm for regular-season scheduling. The Schedule tab can still fine-tune the generated schedule.</p>
      <div className="field">
        <FieldLabel required help="Pick the normal nights this season plays. The schedule builder uses these as defaults and can still be adjusted later.">
          Game nights
        </FieldLabel>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          {WEEKDAYS.map((day) => (
            <button
              key={day.value}
              type="button"
              className={`btn btn--sm ${draft.seasonWeekdays.includes(day.value) ? 'btn--primary' : 'btn--ghost'}`}
              onClick={() => setWeekday(day.value, !draft.seasonWeekdays.includes(day.value))}
            >
              {day.label}
            </button>
          ))}
        </div>
        {hasError(errors, 'weekdays') && <span className="field__error">Choose at least one game night.</span>}
      </div>
      <div className="grid grid--2">
        <div className="field">
          <FieldLabel required help="The first game start time on a normal game night. Individual games can be adjusted in the schedule.">
            First puck time
          </FieldLabel>
          <input
            type="time"
            className="input"
            value={draft.seasonFirstPuckTime}
            onChange={(e) => update({ seasonFirstPuckTime: e.target.value })}
            aria-invalid={hasError(errors, 'firstPuck')}
          />
          {hasError(errors, 'firstPuck') && <span className="field__error">First puck time is required.</span>}
        </div>
        <div className="field">
          <FieldLabel required help="The maximum number of games the scheduler should place on each rink on a normal night.">
            Games per rink/night
          </FieldLabel>
          <input
            type="number"
            min={1}
            max={12}
            className="input"
            value={draft.seasonGamesPerRinkPerNight}
            onChange={(e) => update({ seasonGamesPerRinkPerNight: clampInt(e.target.value, 1, 12) })}
            aria-invalid={hasError(errors, 'gamesPerNight')}
          />
          {hasError(errors, 'gamesPerNight') && <span className="field__error">Enter at least one game per rink/night.</span>}
        </div>
      </div>
      <BlackoutDatePicker draft={draft} update={update} />
    </div>
  );
}

function RulesStep({ draft, errors, update }: StepProps) {
  const setScoring = (patch: Partial<ScoringSettings>) => update({ scoring: { ...draft.scoring, ...patch } });
  const isSeason = draft.format === 'season';
  const periodCount = draft.scoring.periodCount ?? 3;
  const periodLength = draft.scoring.periodLengthMinutes ?? 12;
  const setPeriodCount = (value: number) => {
    setScoring({ periodCount: value, gameLengthMinutes: value * periodLength });
  };
  const setPeriodLength = (value: number) => {
    setScoring({ periodLengthMinutes: value, gameLengthMinutes: periodCount * value });
  };
  const moveTiebreaker = (key: ScoringSettings['tiebreakers'][number], dir: -1 | 1) => {
    const list = [...draft.scoring.tiebreakers];
    const i = list.indexOf(key);
    const j = i + dir;
    if (i < 0 || j < 0 || j >= list.length) return;
    [list[i], list[j]] = [list[j], list[i]];
    setScoring({ tiebreakers: list });
  };

  return (
    <div className="grid grid--2">
      {hasError(errors, 'rules') && <div className="field__error" style={{ gridColumn: '1 / -1' }}>Complete the game rule fields.</div>}
      <div className="card card--glass">
        <div className="card__head"><h3 className="card__title">Game rules</h3></div>
        {isSeason ? (
          <div className="stack">
            <p className="muted" style={{ marginTop: 0 }}>
              Defaults are NCRHA-style for roller hockey. Scorekeeping details like assists, penalties, and goal timestamps are handled in the scoring workflow.
            </p>
            <div className="grid grid--2">
              <div className="field">
                <FieldLabel required help="How many regulation periods each regular-season game has.">
                  Periods
                </FieldLabel>
                <input
                  type="number"
                  min={1}
                  max={6}
                  className="input"
                  value={periodCount}
                  onChange={(e) => setPeriodCount(clampInt(e.target.value, 1, 6))}
                />
              </div>
              <div className="field">
                <FieldLabel required help="Length of each regulation period. Game start times are still controlled by the schedule.">
                  Minutes per period
                </FieldLabel>
                <input
                  type="number"
                  min={1}
                  max={30}
                  className="input"
                  value={periodLength}
                  onChange={(e) => setPeriodLength(clampInt(e.target.value, 1, 30))}
                />
              </div>
            </div>
            <div className="note">Game length: {periodCount * periodLength} regulation minutes.</div>
            <div className="grid grid--2">
              <div className="field">
                <FieldLabel help="House rules can go straight to shootout, use overtime before shootout, allow ties, or use continuous sudden-death overtime.">
                  Tie game rule
                </FieldLabel>
                <select
                  className="select"
                  value={draft.scoring.overtimeFormat ?? 'straight_shootout'}
                  onChange={(e) => setScoring({ overtimeFormat: e.target.value as ScoringSettings['overtimeFormat'] })}
                >
                  <option value="straight_shootout">Straight to shootout</option>
                  <option value="overtime_then_shootout">Overtime, then shootout</option>
                  <option value="continuous_overtime">Continuous sudden-death overtime</option>
                  <option value="regulation_tie">Allow ties after regulation</option>
                </select>
              </div>
              <div className="field">
                <FieldLabel help="Number of shooters before sudden death begins.">
                  Shootout round size
                </FieldLabel>
                <input
                  type="number"
                  min={1}
                  max={10}
                  className="input"
                  value={draft.scoring.shootoutRoundCount ?? 3}
                  onChange={(e) => setScoring({ shootoutRoundCount: clampInt(e.target.value, 1, 10) })}
                />
              </div>
            </div>
            <div className="grid grid--3">
              <div className="field">
                <FieldLabel>OT minutes</FieldLabel>
                <input
                  type="number"
                  min={1}
                  max={20}
                  className="input"
                  value={draft.scoring.overtimeMinutes ?? 5}
                  onChange={(e) => setScoring({ overtimeMinutes: clampInt(e.target.value, 1, 20) })}
                />
              </div>
              <div className="field">
                <FieldLabel>OT skaters</FieldLabel>
                <input
                  type="number"
                  min={1}
                  max={5}
                  className="input"
                  value={draft.scoring.overtimeSkaters ?? 3}
                  onChange={(e) => setScoring({ overtimeSkaters: clampInt(e.target.value, 1, 5) })}
                />
              </div>
              <div className="field">
                <FieldLabel>OT/SO loss points</FieldLabel>
                <input
                  type="number"
                  min={0}
                  max={3}
                  className="input"
                  value={draft.scoring.pointsOvertimeLoss ?? 1}
                  onChange={(e) => {
                    const value = clampInt(e.target.value, 0, 3);
                    setScoring({ pointsOvertimeLoss: value, pointsShootoutLoss: value });
                  }}
                />
              </div>
            </div>
          </div>
        ) : (
          <div className="stack">
            <div className="field">
              <FieldLabel required>Game length (minutes)</FieldLabel>
              <input
                type="number"
                min={1}
                max={120}
                className="input"
                value={draft.scoring.gameLengthMinutes}
                onChange={(e) => setScoring({ gameLengthMinutes: clampInt(e.target.value, 1, 120) })}
              />
            </div>
            <Switch checked={draft.scoring.trackAssists} onChange={(v) => setScoring({ trackAssists: v })} label="Track assists" />
            <Switch checked={draft.scoring.trackPenaltyMinutes} onChange={(v) => setScoring({ trackPenaltyMinutes: v, trackPenaltyShotsOnly: !v && draft.scoring.trackPenaltyShotsOnly })} label="Track penalty minutes" />
            <Switch checked={draft.scoring.trackPenaltyShotsOnly} onChange={(v) => setScoring({ trackPenaltyShotsOnly: v, trackPenaltyMinutes: v ? false : draft.scoring.trackPenaltyMinutes })} label="Penalty shots only" />
            <Switch checked={draft.scoring.trackGoalTimestamps} onChange={(v) => setScoring({ trackGoalTimestamps: v })} label="Capture goal timestamps" />
          </div>
        )}
      </div>
      <div className="card card--glass">
        <div className="card__head">
          <div>
            <h3 className="card__title">Standings tiebreakers</h3>
            <p className="card__sub">Use Up and Down to decide how tied teams are ranked in standings and playoff seeding.</p>
          </div>
        </div>
        <div className="stack">
          {draft.scoring.tiebreakers.map((key, i) => (
            <div key={key} className="row row--between" style={{ gap: 10 }}>
              <span><span className="kbd">{i + 1}</span> {TIEBREAKER_LABEL[key]}</span>
              <span className="row" style={{ gap: 4 }}>
                <button className="btn btn--ghost btn--sm" disabled={i === 0} onClick={() => moveTiebreaker(key, -1)}>Up</button>
                <button className="btn btn--ghost btn--sm" disabled={i === draft.scoring.tiebreakers.length - 1} onClick={() => moveTiebreaker(key, 1)}>Down</button>
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PlayoffsStep({ draft, errors, update }: StepProps) {
  return (
    <div className="stack">
      <Switch
        checked={draft.playoffsEnabled}
        onChange={(v) => update({ playoffsEnabled: v })}
        label="Include playoffs"
        hint="Seeds use final regular-season standings."
      />
      {draft.playoffsEnabled && (
        <>
          <div className="segmented segmented--cols-3">
            {PLAYOFF_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`pick ${draft.playoffFormat === option.value ? 'is-active' : ''}`}
                onClick={() => update({ playoffFormat: option.value })}
              >
                <div className="pick__title">{option.title}</div>
                <div className="pick__sub">{option.description}</div>
              </button>
            ))}
          </div>
          <div className="grid grid--2">
            <div className="field">
              <FieldLabel required help="How many teams qualify from the final regular-season standings.">
                Qualifying teams
              </FieldLabel>
              <input
                type="number"
                min={2}
                max={64}
                className="input"
                value={draft.playoffQualifyingTeams}
                onChange={(e) => update({ playoffQualifyingTeams: clampInt(e.target.value, 2, 64) })}
                aria-invalid={hasError(errors, 'playoffTeams')}
              />
              {hasError(errors, 'playoffTeams') && <span className="field__error">Enter at least two teams.</span>}
            </div>
            <div className="note">Seeding source: final regular-season standings.</div>
          </div>
        </>
      )}
    </div>
  );
}

function ReviewStep({ draft, typeLabel }: { draft: TournamentDraft; typeLabel: string }) {
  const isSeason = draft.format === 'season';
  return (
    <div className="stack">
      <ReviewBlock label={isSeason ? 'Season type' : 'Preset'} value={isSeason ? 'Roller Hockey Season' : typeLabel} />
      <ReviewBlock label="Name" value={draft.name || '(missing)'} />
      <ReviewBlock label="Dates" value={isSeason ? `${formatDate(draft.startsAt)} -> ${formatDate(draft.endsAt)}` : `${formatDt(draft.startsAt)} -> ${formatDt(draft.endsAt)}`} />
      <ReviewBlock label={isSeason ? 'Rink' : 'Venue'} value={`${draft.venueName}, ${[draft.venueAddress, draft.venuePostalCode, draft.venueCity, draft.venueRegion].filter(Boolean).join(', ')}`} />
      {!isSeason && <ReviewBlock label="Rinks" value={String(draft.rinkCount)} />}
      {isSeason && (
        <>
          <ReviewBlock label="Cadence" value={`${draft.seasonWeekdays.join(', ')} at ${draft.seasonFirstPuckTime}; ${draft.seasonGamesPerRinkPerNight} game(s) per rink/night`} />
          <ReviewBlock label="Game structure" value={`${draft.scoring.periodCount ?? 3} period(s) x ${draft.scoring.periodLengthMinutes ?? 12} minutes`} />
          <ReviewBlock label="Tie games" value={formatOvertimeRule(draft.scoring)} />
          <ReviewBlock label="Playoffs" value={draft.playoffsEnabled ? `${draft.playoffFormat.replaceAll('_', ' ')}; top ${draft.playoffQualifyingTeams}` : 'Not included'} />
        </>
      )}
      <ReviewBlock label="Next actions" value="Set up registration, import roster from Directory, create schedule, then publish." />
      <div className="note">
        <Sparkles size={14} style={{ display: 'inline', marginRight: 6, verticalAlign: -2 }} />
        This creates a draft {isSeason ? 'season' : 'tournament'}. Publishing remains a separate action and will not send messages automatically.
      </div>
    </div>
  );
}

interface StepProps {
  draft: TournamentDraft;
  errors?: StepValidation[];
  update: (patch: Partial<TournamentDraft>) => void;
}

function FieldLabel({
  children,
  required,
  optional,
  help,
}: {
  children: ReactNode;
  required?: boolean;
  optional?: boolean;
  help?: string;
}) {
  return (
    <div className="field-label-stack">
      <div className="field-label-row">
        <span className="field__label">{children}</span>
        {required && <span className="field-badge field-badge--required">Required</span>}
        {optional && <span className="field-badge">Optional</span>}
      </div>
      {help && <p className="field__hint">{help}</p>}
    </div>
  );
}

function BlackoutDatePicker({ draft, update }: StepProps) {
  const [pendingDate, setPendingDate] = useState('');
  const dates = draft.seasonBlackoutDates.map(dateOnly).filter(Boolean);
  const canAdd = pendingDate && !dates.includes(pendingDate) && dates.length < 5;

  const add = () => {
    if (!canAdd) return;
    update({ seasonBlackoutDates: [...draft.seasonBlackoutDates, fromDate(pendingDate)] });
    setPendingDate('');
  };
  const remove = (date: string) => {
    update({ seasonBlackoutDates: draft.seasonBlackoutDates.filter((value) => dateOnly(value) !== date) });
  };

  return (
    <div className="field">
      <FieldLabel optional help="Dates the scheduler should skip, such as holidays, rink closures, or league blackout nights. Add up to five here; more can be handled in the schedule later.">
        Blackout dates
      </FieldLabel>
      <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="date"
          className="input"
          value={pendingDate}
          onChange={(event) => setPendingDate(event.target.value)}
          style={{ maxWidth: 220 }}
          disabled={dates.length >= 5}
        />
        <button type="button" className="btn btn--ghost btn--sm" onClick={add} disabled={!canAdd}>
          Add date
        </button>
      </div>
      {dates.length === 0 ? (
        <div className="field__hint">No blackout dates added.</div>
      ) : (
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          {dates.map((date) => (
            <button key={date} type="button" className="chip" onClick={() => remove(date)} title="Remove blackout date">
              {formatDate(fromDate(date))} x
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ReviewBlock({ label, value }: { label: string; value: string }) {
  return (
    <div className="row row--between" style={{ padding: '10px 0', borderBottom: '1px solid var(--surface-line)', gap: 16, alignItems: 'flex-start' }}>
      <div className="eyebrow" style={{ flexShrink: 0 }}>{label}</div>
      <div style={{ fontWeight: 600, textAlign: 'right', minWidth: 0 }}>{value}</div>
    </div>
  );
}

function buildSettings(draft: TournamentDraft, base?: TournamentSettings): TournamentSettings {
  const fallback = draft.format === 'season' ? defaultRollerSeasonSettings() : undefined;
  const source = base ?? fallback;
  if (!source) {
    return {
      scoring: draft.scoring,
      scheduling: { minTeamRestMinutes: 20, rinkTurnoverMinutes: 5, balanceBackToBacks: true },
      modules: { proShop: false, concessions: false, locker: true, gamification: false },
    };
  }

  const settings: TournamentSettings = {
    ...source,
    scoring: draft.format === 'season' ? draft.scoring : source.scoring,
  };
  if (draft.format === 'season') {
    settings.season = {
      ...(source.season ?? defaultRollerSeasonSettings().season!),
      weekdays: draft.seasonWeekdays,
      firstPuckTime: draft.seasonFirstPuckTime,
      gamesPerRinkPerNight: draft.seasonGamesPerRinkPerNight,
      regularSeasonEndsAt: draft.endsAt,
      blackoutDates: draft.seasonBlackoutDates,
      rosterIntake: 'later',
      registration: {
        enabled: false,
        intakeType: draft.registrationIntakeType,
        opensAt: draft.registrationOpensAt || undefined,
        closesAt: draft.registrationClosesAt || undefined,
        feeCents: draft.registrationFeeCents,
        requireWaiver: draft.registrationRequireWaiver,
        autoApprove: draft.registrationAutoApprove,
      },
      halfSplit: source.season?.halfSplit ?? false,
    };
    settings.playoffs = draft.playoffsEnabled
      ? {
          ...(source.playoffs ?? defaultPlayoffSettings()),
          format: draft.playoffFormat,
          qualifyingTeams: draft.playoffQualifyingTeams,
          seedingSource: 'final_standings',
        }
      : undefined;
  }
  return settings;
}

function draftFromSettings(draft: TournamentDraft, settings: TournamentSettings): Partial<TournamentDraft> {
  const patch: Partial<TournamentDraft> = {
    scoring: settings.scoring,
  };
  if (draft.format === 'season' && settings.season) {
    patch.seasonWeekdays = settings.season.weekdays;
    patch.seasonFirstPuckTime = settings.season.firstPuckTime;
    patch.seasonGamesPerRinkPerNight = settings.season.gamesPerRinkPerNight;
    patch.seasonRegularSeasonEndsAt = settings.season.regularSeasonEndsAt ?? draft.seasonRegularSeasonEndsAt;
    patch.seasonBlackoutDates = settings.season.blackoutDates;
    patch.rosterChoice = settings.season.rosterIntake;
    patch.registrationIntakeType = settings.season.registration.intakeType;
    patch.registrationFeeCents = settings.season.registration.feeCents;
    patch.registrationRequireWaiver = settings.season.registration.requireWaiver;
    patch.registrationAutoApprove = settings.season.registration.autoApprove;
    patch.playoffsEnabled = Boolean(settings.playoffs);
    patch.playoffFormat = settings.playoffs?.format ?? 'single_elim';
    patch.playoffQualifyingTeams = settings.playoffs?.qualifyingTeams ?? 4;
    patch.playoffSeedingSource = settings.playoffs?.seedingSource ?? 'final_standings';
  }
  return patch;
}

function normalizeStep(draft: TournamentDraft, count: number): TournamentDraft {
  return { ...draft, step: Math.min(Math.max(draft.step, 0), count - 1) };
}

function validateDraft(draft: TournamentDraft, steps: readonly string[]): StepValidation[] {
  return steps.flatMap((step) => validateStep(draft, step));
}

function validateStep(draft: TournamentDraft, stepName: string): StepValidation[] {
  const errors: StepValidation[] = [];
  if (stepName === 'Type' && !draft.typeId) {
    errors.push({ code: 'type', message: 'Choose a preset.' });
  }
  if (stepName === 'Details') {
    if (!draft.name.trim()) errors.push({ code: 'name', message: 'Add a name.' });
    if (!draft.startsAt || !draft.endsAt) errors.push({ code: 'dates', message: 'Add start and end dates.' });
  }
  if (stepName === 'Venue' || stepName === 'Rink') {
    if (!draft.venueName.trim()) errors.push({ code: 'venueName', message: 'Add the rink or venue name.' });
    if (!draft.venueAddress.trim()) errors.push({ code: 'venueAddress', message: 'Add the rink or venue address.' });
    if (draft.rinkCount < 1) errors.push({ code: 'rinkCount', message: 'Add at least one rink.' });
  }
  if (stepName === 'Cadence') {
    if (draft.seasonWeekdays.length === 0) errors.push({ code: 'weekdays', message: 'Choose at least one game night.' });
    if (!draft.seasonFirstPuckTime) errors.push({ code: 'firstPuck', message: 'Add the first puck time.' });
    if (draft.seasonGamesPerRinkPerNight < 1) errors.push({ code: 'gamesPerNight', message: 'Add games per rink/night.' });
  }
  if (stepName === 'Rules') {
    const periodCount = draft.scoring.periodCount ?? 0;
    const periodLength = draft.scoring.periodLengthMinutes ?? 0;
    const valid = draft.format === 'season'
      ? periodCount > 0 && periodLength > 0 && draft.scoring.tiebreakers.length > 0
      : draft.scoring.gameLengthMinutes > 0 && draft.scoring.tiebreakers.length > 0;
    if (!valid) errors.push({ code: 'rules', message: 'Complete the game rules.' });
  }
  if (stepName === 'Playoffs' && draft.playoffsEnabled && draft.playoffQualifyingTeams < 2) {
    errors.push({ code: 'playoffTeams', message: 'Playoffs need at least two qualifying teams.' });
  }
  return errors;
}

function hasError(errors: StepValidation[] | undefined, code: StepValidationCode): boolean {
  return Boolean(errors?.some((error) => error.code === code));
}

function toLocal(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocal(local: string): string {
  if (!local) return '';
  return new Date(local).toISOString();
}

function fromDate(date: string): string {
  if (!date) return '';
  return new Date(`${date}T00:00:00`).toISOString();
}

function dateOnly(iso: string): string {
  if (!iso) return '';
  return new Date(iso).toISOString().slice(0, 10);
}

function formatDt(iso: string): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleString();
}

function formatDate(iso: string): string {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatOvertimeRule(scoring: ScoringSettings): string {
  switch (scoring.overtimeFormat ?? 'straight_shootout') {
    case 'regulation_tie':
      return 'Allow ties after regulation';
    case 'overtime_then_shootout':
      return `${scoring.overtimeMinutes ?? 5} min ${scoring.overtimeSkaters ?? 3}-on-${scoring.overtimeSkaters ?? 3} OT, then ${scoring.shootoutRoundCount ?? 3}-player shootout`;
    case 'continuous_overtime':
      return `Continuous sudden-death overtime`;
    case 'straight_shootout':
    default:
      return `${scoring.shootoutRoundCount ?? 3}-player shootout after regulation`;
  }
}

function clampInt(value: string, min: number, max: number): number {
  const parsed = Number.parseInt(value || String(min), 10);
  if (Number.isNaN(parsed)) return min;
  return Math.max(min, Math.min(max, parsed));
}

async function lookupZip(zip: string): Promise<{ city: string; stateCode: string } | null> {
  try {
    const response = await fetch(`/api/geo/zip?zip=${encodeURIComponent(zip)}`, { credentials: 'same-origin' });
    if (!response.ok) return null;
    const data = (await response.json()) as { city?: string; stateCode?: string };
    return data.city && data.stateCode ? { city: data.city, stateCode: data.stateCode } : null;
  } catch {
    return null;
  }
}
